/**
 * Header strip above the conversation.
 *
 * Carries the things that were previously unreachable or invisible: which
 * engine is actually answering, what the assistant can see of the workspace,
 * and a way to get the conversation out of the browser.
 *
 * The engine pill matters more than it looks. `/ai_assistant/chat` silently
 * falls back to canned rule-based replies when `GEMINI_API_KEY` is missing or
 * rejected, and the old screen rendered those identically to a real answer — so
 * a misconfigured deploy read as "the AI got much worse" with nothing on screen
 * to explain it.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useI18n } from '../../../i18n';
import Badge from '../../../components/Atoms/Badge';
import useAssistantStore from '../store/useAssistantStore';

const AssistantTopBar = ({ thread, status, workspace, onExport, onCopyAll, onNewChat }) => {
  const { t } = useI18n();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  const toggleSidebar = useAssistantStore((state) => state.toggleSidebar);
  const sidebarCollapsed = useAssistantStore((state) => state.sidebarCollapsed);
  const toggleSidebarCollapsed = useAssistantStore((state) => state.toggleSidebarCollapsed);
  const contextPanelOpen = useAssistantStore((state) => state.contextPanelOpen);
  const setContextPanelOpen = useAssistantStore((state) => state.setContextPanelOpen);

  // Close the export menu on an outside click or Escape.
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onPointerDown = (event) => {
      if (!menuRef.current?.contains(event.target)) setMenuOpen(false);
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [menuOpen]);

  const hasMessages = (thread?.messages || []).length > 0;
  const limited = status && status.geminiConfigured === false;

  const run = useCallback(
    (action) => () => {
      setMenuOpen(false);
      action?.();
    },
    [],
  );

  return (
    <header className="lf-topbar">
      <button
        type="button"
        className="lf-topbar__icon lf-only-md"
        onClick={toggleSidebar}
        title={t('assistant.sidebar.heading')}
        aria-label={t('assistant.sidebar.heading')}
      >
        <i className="fa fa-bars" aria-hidden="true" />
      </button>

      <button
        type="button"
        className="lf-topbar__icon lf-hide-md"
        onClick={toggleSidebarCollapsed}
        title={
          sidebarCollapsed ? t('assistant.topbar.expandSidebar') : t('assistant.topbar.collapseSidebar')
        }
        aria-label={
          sidebarCollapsed ? t('assistant.topbar.expandSidebar') : t('assistant.topbar.collapseSidebar')
        }
        aria-pressed={sidebarCollapsed}
      >
        <i className={`fa ${sidebarCollapsed ? 'fa-chevron-right' : 'fa-chevron-left'}`} aria-hidden="true" />
      </button>

      <div className="lf-topbar__title">
        <h1 className="lf-topbar__heading">
          {thread?.title || t('assistant.title')}
        </h1>
        {limited ? (
          <Badge variant="warning" icon="fa-exclamation-triangle">
            {t('assistant.badge.limited')}
          </Badge>
        ) : (
          status && (
            <Badge variant="success" icon="fa-bolt">
              {t('assistant.badge.ready')}
            </Badge>
          )
        )}
      </div>

      <span className="lf-topbar__spacer" />

      {!!workspace && (
        <button
          type="button"
          className={`lf-topbar__icon${contextPanelOpen ? ' lf-topbar__icon--active' : ''}`}
          onClick={() => setContextPanelOpen(!contextPanelOpen)}
          title={t('assistant.topbar.workspace')}
          aria-label={t('assistant.topbar.workspace')}
          aria-pressed={contextPanelOpen}
        >
          <i className="fa fa-database" aria-hidden="true" />
        </button>
      )}

      <div className="lf-topbar__menu" ref={menuRef}>
        <button
          type="button"
          className="lf-topbar__icon"
          onClick={() => setMenuOpen((open) => !open)}
          disabled={!hasMessages}
          title={t('assistant.topbar.more')}
          aria-label={t('assistant.topbar.more')}
          aria-haspopup="menu"
          aria-expanded={menuOpen}
        >
          <i className="fa fa-ellipsis-h" aria-hidden="true" />
        </button>
        {menuOpen && (
          <div className="lf-topbar__dropdown" role="menu">
            <button type="button" role="menuitem" onClick={run(onExport)}>
              <i className="fa fa-download" aria-hidden="true" />
              {t('assistant.topbar.export')}
            </button>
            <button type="button" role="menuitem" onClick={run(onCopyAll)}>
              <i className="fa fa-copy" aria-hidden="true" />
              {t('assistant.topbar.copyAll')}
            </button>
          </div>
        )}
      </div>

      <button type="button" className="lf-topbar__new" onClick={onNewChat}>
        <i className="fa fa-plus" aria-hidden="true" />
        <span className="lf-hide-sm">{t('assistant.sidebar.newChat')}</span>
      </button>
    </header>
  );
};

export default AssistantTopBar;
